'use client';
import { usePreventScroll } from '@react-aria/overlays';
import PropTypes from 'prop-types';
import * as React from 'react';
import { useComponentStylesLoaded } from '../../hooks/use-component-styles-loaded.js';
import { useTransition } from '../../hooks/use-transition.js';
import { ModalDialogContext } from './modal-dialog.context.js';
import { ModalDialogVariantEnum, ModalDialogBodyWidths } from './modal-dialog.types.js';
import { getComponentConfigProvider } from '../../contexts/internal/component-config/component-config.component.js';
import { isFunction } from '../../../core/utilities/is.utils.js';
import { filterProps } from '../core/core.utils.js';
import { renderWithRef } from '../core/render-with-ref.js';
import { SWAN_STYLE_KEY_MAP } from '../head/stylesheets.component.js';
import { processStyleProps } from '../../../core/utilities/swan-style.js';
import { RenderComp } from '../core/render-comp.component.js';
import { assignRefs } from '../../../core/utilities/ref.utils.js';

function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const dialogPropTypes = {
    /**
   * Specify Modal type
   * @default standard
   */ variant: PropTypes.oneOf([
        ModalDialogVariantEnum.Menu,
        ModalDialogVariantEnum.Standard,
        ModalDialogVariantEnum.PanelRight,
        ModalDialogVariantEnum.PanelTop,
        ModalDialogVariantEnum.PanelLeft,
        ModalDialogVariantEnum.PanelBottom
    ]),
    /**
   * Callback function which will be invoked when the modal wants to be dismissed.
   */ onRequestDismiss: PropTypes.func.isRequired,
    /**
   * Decide whether to fill the full screen.
   */ takeOver: PropTypes.bool,
    /**
   * Width of the dialog body. Some dialog variants allow the body width to be limited (capped)
   */ bodyWidth: PropTypes.oneOf(ModalDialogBodyWidths),
    /**
   * @internal
   * @ignore
   * This is not a part of the public API yet
   * Decide whether dialog is modal or modeless
   * @default false
   */ isModal: PropTypes.bool,
    /**
   * Show/hide dialog
   */ isOpen: PropTypes.bool,
    /**
   * only renders the contents of the dialog if the modal is open
   * this is different than native dialog behavior, where the content is rendered but hidden
   * @default false
   */ onlyRenderWhenOpen: PropTypes.bool
};
const [DialogConfigProvider, useInternalDialogConfigContext, DialogConfigContext] = getComponentConfigProvider('Dialog');
const propKeysToRemove = Object.keys(dialogPropTypes);
const Dialog = renderWithRef('Dialog', dialogPropTypes, (props, ref)=>{
    useComponentStylesLoaded('Dialog', SWAN_STYLE_KEY_MAP.modalDialog);
    const { bgc, backgroundColor } = props, otherProps = _object_without_properties(props, [
        "bgc",
        "backgroundColor"
    ]);
    const { variant = 'standard', onRequestDismiss, takeOver, bodyWidth, isOpen, children, isModal = false, onlyRenderWhenOpen = false } = otherProps;
    const [showDialog, setShowDialog] = React.useState(!!isOpen);
    const [titleId, setTitleId] = React.useState(undefined);
    const [footerPinned, setFooterPinned] = React.useState(undefined);
    const [canTriggerDialog, setTriggerDialog] = React.useState(typeof window !== 'undefined' && typeof window.HTMLDialogElement !== 'undefined') // wait to open dialog while registering the polyfill
    ;
    const transitionState = useTransition(!!isOpen);
    // prevent the page scroll
    usePreventScroll({
        isDisabled: !showDialog
    });
    const dialogRef = React.useRef(null);
    const portalRef = React.useRef(null);
    const combinedRef = assignRefs(ref, dialogRef);
    const closeModalDialog = React.useCallback(()=>{
        var _dialogRef_current;
        // trigger the dialog close ONLY when the dialog is opened
        if ((_dialogRef_current = dialogRef.current) === null || _dialogRef_current === void 0 ? void 0 : _dialogRef_current.open) {
            var _dialogRef_current1;
            (_dialogRef_current1 = dialogRef.current) === null || _dialogRef_current1 === void 0 ? void 0 : _dialogRef_current1.close();
        }
    }, []);
    // trigger when modal get closed
    const handleOnDialogClose = React.useCallback((event)=>{
        setShowDialog(false);
        if (isFunction(onRequestDismiss) && event.target === dialogRef.current) {
            onRequestDismiss();
        }
    }, [
        onRequestDismiss
    ]);
    React.useEffect(()=>{
        if (!canTriggerDialog || !dialogRef.current) {
            return;
        }
        if (isOpen) {
            setShowDialog(true);
            if (isModal) {
                dialogRef.current.showModal();
            } else {
                dialogRef.current.show();
            }
        } else {
            closeModalDialog();
        }
    }, [
        isOpen,
        isModal,
        canTriggerDialog,
        closeModalDialog
    ]);
    React.useEffect(()=>{
        const dialogEle = dialogRef.current;
        let intervalId = undefined;
        if (dialogEle && !window.HTMLDialogElement) {
            intervalId = setInterval(()=>{
                const dialogPolyfill = window.dialogPolyfill;
                if (dialogPolyfill) {
                    dialogPolyfill.registerDialog(dialogEle);
                    setTriggerDialog(true);
                    clearInterval(intervalId);
                }
            }, 200);
        }
        return ()=>{
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, []);
    React.useEffect(()=>{
        const dialogEle = dialogRef.current;
        let isDragging = false;
        const handleMouseDown = ()=>{
            isDragging = false;
        };
        const handleMouseMove = ()=>{
            isDragging = true;
        };
        const handleClick = (event)=>{
            // close the dialog when the user clicks outside the dialog body but not dragging
            if (isModal && event.target === dialogRef.current && !isDragging) {
                closeModalDialog();
            }
        };
        if (dialogEle) {
            dialogEle.addEventListener('mousedown', handleMouseDown);
            dialogEle.addEventListener('mousemove', handleMouseMove);
            dialogEle.addEventListener('click', handleClick);
            dialogEle.addEventListener('close', handleOnDialogClose);
            return ()=>{
                dialogEle.removeEventListener('mousedown', handleMouseDown);
                dialogEle.removeEventListener('mousemove', handleMouseMove);
                dialogEle.removeEventListener('click', handleClick);
                dialogEle.removeEventListener('close', handleOnDialogClose);
            };
        }
    }, [
        closeModalDialog,
        handleOnDialogClose,
        isModal
    ]);
    const classNames = new Set([
        'swan-dialog',
        'swan-vanilla-ignore'
    ]);
    if (!isModal) classNames.add('swan-dialog-modeless');
    const classNameIter = processStyleProps(otherProps);
    // filter out any props on the Dialog that we actually want to apply to the ModalDialogContent instead
    const dialogProps = filterProps(props, [
        'backgroundColor',
        'bgc'
    ], classNameIter);
    // a11y support for browsers that don't natively recognize the <dialog> tag
    dialogProps['role'] = 'dialog';
    dialogProps['aria-modal'] = isModal ? 'true' : 'false';
    // aria-labelledby set to title id when not supplied directly
    if (!props['aria-label'] && !props['aria-labelledby'] && titleId) {
        dialogProps['aria-labelledby'] = titleId;
    }
    return /*#__PURE__*/ React.createElement(ModalDialogContext.Provider, {
        value: {
            variant,
            transitionState,
            handleModalCloseButton: closeModalDialog,
            titleId,
            setTitleId,
            bgc,
            backgroundColor,
            takeOver: takeOver !== null && takeOver !== void 0 ? takeOver : false,
            isModal: isModal !== null && isModal !== void 0 ? isModal : false,
            bodyWidth: bodyWidth || 'standard',
            footerPinned,
            setFooterPinned
        }
    }, /*#__PURE__*/ React.createElement(DialogConfigProvider, {
        config: {
            inModal: true,
            portal: false,
            portalRef,
            dialogRef
        }
    }, /*#__PURE__*/ React.createElement(RenderComp, {
        root: "dialog",
        forwardedRef: combinedRef,
        classNames: classNames,
        props: dialogProps,
        propKeysToRemove: propKeysToRemove
    }, !onlyRenderWhenOpen || isOpen ? children : null)));
});

export { Dialog, DialogConfigContext, dialogPropTypes, useInternalDialogConfigContext };
